import { useCallback, useMemo, useState } from "react"
import Cropper, { type Point, type Area, type Size } from "react-easy-crop"
import { Modal } from "components/ui/modals/Modal/Modal"
import { ButtonPrimary } from "components/ui/buttons/ButtonPrimary/ButtonPrimary"
import { ButtonSecondary } from "components/ui/buttons/ButtonSecondary/ButtonSecondary"
import { cropImageToBlob } from "helpers/cropImage.helper"
import style from "./EditImageModal.module.scss"

const { cropperWrapper, cornersOverlay, corner, cornerTL, cornerTR, cornerBL, cornerBR, buttonsBlock } = style

type Props = {
	isModalOpen: boolean
	setModalClose: () => void
	image: File | null
	onSave: (croppedUrl: string) => void
	isSaving?: boolean
}

export const EditImageModal = ({ isModalOpen, setModalClose, image, onSave, isSaving = false }: Props) => {
	const [crop, setCrop] = useState<Point>({ x: 0, y: 0 })
	const [zoom, setZoom] = useState(1)
	const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null)
	const [cropSize, setCropSize] = useState<Size | null>(null)

	const imageUrl = useMemo(() => (image ? URL.createObjectURL(image) : null), [image])

	const onCropComplete = useCallback((_: Area, croppedPixels: Area) => {
		setCroppedAreaPixels(croppedPixels)
	}, [])

	const handleSave = () => {
		if (!imageUrl || !croppedAreaPixels) return

		cropImageToBlob(imageUrl, croppedAreaPixels).then((croppedUrl) => {
			onSave(croppedUrl)
		})
	}

	return (
		<Modal isModalOpen={isModalOpen} setModalClose={setModalClose} width={500} title="Edit image">
			<div data-testid="edit-image-modal">
				{imageUrl && (
					<div className={cropperWrapper}>
						<Cropper
							image={imageUrl}
							crop={crop}
							zoom={zoom}
							onCropChange={setCrop}
							onCropComplete={onCropComplete}
							onZoomChange={setZoom}
							onCropSizeChange={setCropSize}
						/>
						{cropSize && (
							<div className={cornersOverlay} style={{ width: cropSize.width, height: cropSize.height }}>
								<span className={`${corner} ${cornerTL}`} />
								<span className={`${corner} ${cornerTR}`} />
								<span className={`${corner} ${cornerBL}`} />
								<span className={`${corner} ${cornerBR}`} />
							</div>
						)}
					</div>
				)}
				<div className={buttonsBlock}>
					<ButtonSecondary onClick={setModalClose}>Cancel</ButtonSecondary>
					<ButtonPrimary onClick={handleSave} submittable={!isSaving}>
						Save
					</ButtonPrimary>
				</div>
			</div>
		</Modal>
	)
}
